import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  LogOut,
  LayoutDashboard,
  Mail,
  MessageSquare,
  HelpCircle,
  FileText,
  Images,
  MapPin,
  Settings,
  Menu,
  X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { SITE } from "../lib/site";
import { API, TOKEN_KEY } from "./admin/adminApi";
import DashboardOverview from "./admin/DashboardOverview";
import ContactsAdmin from "./admin/ContactsAdmin";
import TestimonialsAdmin from "./admin/TestimonialsAdmin";
import FaqAdmin from "./admin/FaqAdmin";
import BlogAdmin from "./admin/BlogAdmin";
import RealisationsAdmin from "./admin/RealisationsAdmin";
import SettingsAdmin from "./admin/SettingsAdmin";
import CommunesAdmin from "./admin/CommunesAdmin";

const SECTIONS = [
  { key: "dashboard", label: "Tableau de bord", icon: LayoutDashboard },
  { key: "contacts", label: "Demandes", icon: Mail },
  { key: "realisations", label: "Réalisations", icon: Images },
  { key: "communes", label: "Communes", icon: MapPin },
  { key: "testimonials", label: "Avis clients", icon: MessageSquare },
  { key: "faq", label: "FAQ", icon: HelpCircle },
  { key: "blog", label: "Blog", icon: FileText },
  { key: "settings", label: "Paramètres", icon: Settings },
];

function LoginScreen({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      toast.success("Connexion réussie");
      onSuccess(data.access_token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Mot de passe incorrect");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFDFC] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src={SITE.logo}
            alt="K'REUSE EXTÉRIEUR"
            className="h-20 w-20 mx-auto rounded-full object-cover ring-4 ring-[#E8F5E9] mb-5"
          />
          <h1 className="font-['Outfit'] text-3xl font-medium text-[#1A2E1C]">
            Espace administrateur
          </h1>
          <p className="text-[#5C705E] mt-2">K'REUSE EXTÉRIEUR</p>
        </div>
        <form
          onSubmit={submit}
          data-testid="admin-login-form"
          className="bg-white border border-[#E5EBE5] rounded-3xl p-8 shadow-sm space-y-5"
        >
          <div>
            <Label htmlFor="password" className="text-[#1A2E1C] mb-2 block">
              Mot de passe
            </Label>
            <Input
              id="password"
              type="password"
              data-testid="admin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoFocus
              className="bg-[#FDFDFC] border-[#E5EBE5] rounded-xl h-12 focus-visible:ring-[#4CAF50]"
              required
            />
          </div>
          <Button
            type="submit"
            data-testid="admin-login-button"
            disabled={loading || !password}
            className="w-full bg-[#1B5E20] hover:bg-[#164d1a] text-white rounded-full h-12 font-medium disabled:opacity-60"
          >
            {loading ? "Connexion..." : "Se connecter"}
          </Button>
        </form>
        <a href="/" className="block text-center mt-6 text-sm text-[#5C705E] hover:text-[#1B5E20]">
          ← Retour au site
        </a>
      </div>
    </div>
  );
}

function DashboardShell({ token, onLogout }) {
  const [active, setActive] = useState("dashboard");
  const [mobileOpen, setMobileOpen] = useState(false);

  const unauthorized = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    onLogout();
  }, [onLogout]);

  const renderSection = () => {
    switch (active) {
      case "dashboard":
        return <DashboardOverview token={token} onUnauthorized={unauthorized} />;
      case "contacts":
        return <ContactsAdmin token={token} onUnauthorized={unauthorized} />;
      case "testimonials":
        return <TestimonialsAdmin token={token} onUnauthorized={unauthorized} />;
      case "faq":
        return <FaqAdmin token={token} onUnauthorized={unauthorized} />;
      case "blog":
        return <BlogAdmin token={token} onUnauthorized={unauthorized} />;
      case "realisations":
        return <RealisationsAdmin token={token} onUnauthorized={unauthorized} />;
      case "communes":
        return <CommunesAdmin token={token} onUnauthorized={unauthorized} />;
      case "settings":
        return <SettingsAdmin token={token} />;
      default:
        return null;
    }
  };

  const goTo = (key) => {
    setActive(key);
    setMobileOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#FDFDFC]">
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between gap-3 px-5 h-16 bg-white border-b border-[#E5EBE5]">
        <button
          onClick={() => setMobileOpen(true)}
          className="p-2 rounded-lg hover:bg-[#E8F5E9]"
          data-testid="admin-mobile-menu"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <img src={SITE.logo} alt="" className="h-8 w-8 rounded-full" />
          <span className="font-['Outfit'] font-semibold text-[#1A2E1C] text-sm">Admin</span>
        </div>
        <button
          onClick={onLogout}
          className="p-2 rounded-lg hover:bg-red-50 text-[#D32F2F]"
          data-testid="admin-logout-mobile"
        >
          <LogOut className="w-5 h-5" />
        </button>
      </div>

      <div className="lg:grid lg:grid-cols-[280px_1fr]">
        {/* Sidebar */}
        <aside
          className={`fixed lg:sticky inset-y-0 left-0 top-0 z-40 w-72 bg-white border-r border-[#E5EBE5] transform transition-transform lg:transform-none ${
            mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
          } lg:h-screen overflow-y-auto`}
          data-testid="admin-sidebar"
        >
          <div className="p-6 border-b border-[#E5EBE5] flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <img src={SITE.logo} alt="" className="h-10 w-10 rounded-full object-cover" />
              <div>
                <div className="font-['Outfit'] font-semibold text-[#1A2E1C] text-sm">
                  K'REUSE EXTÉRIEUR
                </div>
                <div className="text-xs text-[#5C705E]">Admin</div>
              </div>
            </div>
            <button
              onClick={() => setMobileOpen(false)}
              className="lg:hidden p-1 rounded hover:bg-[#E8F5E9]"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <nav className="p-4 space-y-1">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              return (
                <button
                  key={s.key}
                  onClick={() => goTo(s.key)}
                  data-testid={`admin-nav-${s.key}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                    active === s.key
                      ? "bg-[#1B5E20] text-white shadow"
                      : "text-[#5C705E] hover:bg-[#E8F5E9] hover:text-[#1B5E20]"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {s.label}
                </button>
              );
            })}
          </nav>

          <div className="p-4 mt-auto border-t border-[#E5EBE5]">
            <Button
              onClick={onLogout}
              variant="outline"
              data-testid="admin-logout-desktop"
              className="w-full rounded-full border-[#E5EBE5] text-[#D32F2F] hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
            <a
              href="/"
              className="block text-center mt-3 text-xs text-[#5C705E] hover:text-[#1B5E20]"
            >
              ← Retour au site
            </a>
          </div>
        </aside>

        {/* Mobile overlay */}
        {mobileOpen && (
          <div
            className="lg:hidden fixed inset-0 z-30 bg-black/30"
            onClick={() => setMobileOpen(false)}
          />
        )}

        {/* Content */}
        <main className="p-5 md:p-8 lg:p-10 max-w-6xl mx-auto w-full">
          {renderSection()}
        </main>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      });
  }, [token]);

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  return (
    <>
      {token ? (
        <DashboardShell token={token} onLogout={handleLogout} />
      ) : (
        <LoginScreen onSuccess={(t) => setToken(t)} />
      )}
    </>
  );
}
